import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import ClockBoard from '../time/ClockBoard';
import {
  IcoCalendar, IcoUsers, IcoCheckCircle,
  IcoSettings, IcoBriefcase, IcoBuilding,
  IcoClock, IcoDollar,
} from './DashIcons';
import '../../styles/admin-dashboard.css';

function AdminDashboard() {
  const { user } = useAuth();
  const { features } = useOrgSettings();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdminAssistant = user?.role === 'adminassistant';
  const isSuperAdminUser = user?.role === 'superadmin';
  const isAdminUser      = user?.role === 'admin';

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      const response = await fetch('/api/dashboard/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setStats(await response.json());
      else console.error('Dashboard stats error:', response.status);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">

        {/* Welcome Section */}
        <section className="welcome-section">
          <h1>Hello {user.firstName} {user.lastName}</h1>
          <p className="subtitle">What would you like to do next?</p>
        </section>

        {/* Live team clock board — only shown when clockInOut feature is enabled */}
        {features.clockInOut && <ClockBoard />}

        {/* Mobile Quick Actions */}
        {isAdminAssistant ? (
          features.scheduling && (
            <div className="mobile-quick-actions" style={{ gridTemplateColumns: 'repeat(1, 1fr)' }}>
              <button className="mobile-quick-action-btn schedule" onClick={() => navigate('/schedule')}>
                <span className="btn-icon"><IcoCalendar size={18} color="currentColor"/></span>
                Schedule
              </button>
            </div>
          )
        ) : (
          <div className="mobile-quick-actions" style={{ gridTemplateColumns: `repeat(${1 + (features.payroll ? 1 : 0) + (features.scheduling ? 1 : 0)}, 1fr)` }}>
            <button className="mobile-quick-action-btn time-entry" onClick={() => navigate('/time-cards')}>
              <span className="btn-icon"><IcoClock size={18} color="currentColor"/></span>
              Add Time Entry
            </button>
            {features.payroll && (
              <button className="mobile-quick-action-btn payroll" onClick={() => navigate('/payroll')}>
                <span className="btn-icon"><IcoDollar size={18} color="currentColor"/></span>
                Run Payroll
              </button>
            )}
            {features.scheduling && (
              <button className="mobile-quick-action-btn schedule" onClick={() => navigate('/schedule')}>
                <span className="btn-icon"><IcoCalendar size={18} color="currentColor"/></span>
                Schedule
              </button>
            )}
          </div>
        )}

        <div className="dashboard-grid">
          {/* Left Column */}
          <div className="left-column">

            {/* Stats Overview — only shown if stats loaded successfully */}
            {!loading && stats && (
              <div className="stats-overview">
                <div className="stat-card">
                  <div className="stat-icon"><IcoUsers size={22} color="#22c55e"/></div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.stats.totalEmployees}</div>
                    <div className="stat-label">Total Employees</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><IcoCheckCircle size={22} color="#22c55e"/></div>
                  <div className="stat-content">
                    <div className="stat-value">{stats.stats.pendingApprovals}</div>
                    <div className="stat-label">Pending Approvals</div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Cards — grid tiles */}
            {(isSuperAdminUser || isAdminUser || isAdminAssistant) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}>

                <div onClick={() => navigate('/team')} style={{ background: 'white', borderRadius: '16px', padding: '2rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.75rem', transition: 'box-shadow 0.2s', border: '2px solid #15803d' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(34,197,94,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.1)'}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IcoUsers size={28} color="#16a34a"/>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111827', marginBottom: '0.35rem' }}>Team Directory</div>
                    <div style={{ fontSize: '0.95rem', color: 'var(--ce-dash-text-soft)', lineHeight: '1.4' }}>Add, edit or delete team members.</div>
                  </div>
                </div>

                <div onClick={() => navigate('/setup-managers')} style={{ background: 'white', borderRadius: '16px', padding: '2rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.75rem', transition: 'box-shadow 0.2s', border: '2px solid #15803d' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(99,102,241,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.1)'}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IcoBriefcase size={28} color="#4338ca"/>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111827', marginBottom: '0.35rem' }}>Setup Managers</div>
                    <div style={{ fontSize: '0.95rem', color: 'var(--ce-dash-text-soft)', lineHeight: '1.4' }}>Create manager accounts and assign employees to their teams.</div>
                  </div>
                </div>

                <div onClick={() => navigate('/setup-departments')} style={{ background: 'white', borderRadius: '16px', padding: '2rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.75rem', transition: 'box-shadow 0.2s', border: '2px solid #15803d' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(168,85,247,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.1)'}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg,#f3e8ff,#e9d5ff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IcoBuilding size={28} color="#7c3aed"/>
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111827', marginBottom: '0.35rem' }}>Setup Departments</div>
                    <div style={{ fontSize: '0.95rem', color: 'var(--ce-dash-text-soft)', lineHeight: '1.4' }}>Group employees into departments for easier organization.</div>
                  </div>
                </div>

                {features.scheduling && (
                  <div onClick={() => navigate('/schedule')} style={{ background: 'white', borderRadius: '16px', padding: '2rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.75rem', transition: 'box-shadow 0.2s', border: '2px solid #15803d' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(13,148,136,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.1)'}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg,#ccfbf1,#99f6e4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IcoCalendar size={28} color="#0d9488"/>
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111827', marginBottom: '0.35rem' }}>Schedule</div>
                      <div style={{ fontSize: '0.95rem', color: 'var(--ce-dash-text-soft)', lineHeight: '1.4' }}>Build and manage weekly schedules for your team.</div>
                    </div>
                  </div>
                )}

                {isSuperAdminUser && (
                  <div onClick={() => navigate('/settings')} style={{ background: 'white', borderRadius: '16px', padding: '2rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.75rem', transition: 'box-shadow 0.2s', border: '2px solid #15803d' }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 12px rgba(245,158,11,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.1)'}>
                    <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg,#fef3c7,#fde68a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IcoSettings size={28} color="#d97706"/>
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111827', marginBottom: '0.35rem' }}>Time Clock Settings</div>
                      <div style={{ fontSize: '0.95rem', color: 'var(--ce-dash-text-soft)', lineHeight: '1.4' }}>Configure punch rounding, pay periods, and kiosk stations.</div>
                    </div>
                  </div>
                )}

              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
